import type { Instructor } from '@/types';
import { ic, sized } from '@/components/Icons';
import { DataTable, Pagination, type Column } from '@/components/ui';
import { COUNTRIES, ds } from '@/data';
import { useI18n } from '@/i18n';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';
import { instrInitials } from '@/screens/instructors/InstrAvatar';

type Row = Instructor & { _key: string; _asg: number };

/** Instructors assigned to one site, nested at the bottom of its detail. */
export function SiteInstructors({ siteId }: { siteId: string }) {
  const { lang } = useI18n();
  const go = useGo();
  const table = useTableState(10);

  const list = ds.listInstructors({ siteId });
  const countryName = (code: string) => {
    const c = COUNTRIES.find((x) => x.code === code);
    return c ? (lang === 'es' ? c.name_es : c.name_en) : code || '';
  };

  const enriched: Row[] = list.map((i) => ({
    ...i,
    _key: i.InstructorId,
    _asg: ds.listAssignments({ instructorId: i.InstructorId }).length,
  }));

  const sortValues: SortMap<Row> = {
    name: { type: 'text', get: (r) => `${r.EnglishLastName || ''} ${r.EnglishFirstName || ''}`.trim() },
    JobTitle: { type: 'text', get: (r) => r.JobTitle || '' },
    country: { type: 'text', get: (r) => countryName(r.Country) },
    cert: { type: 'text', get: (r) => r.Certification || '' },
    asg: { type: 'number', get: (r) => r._asg },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      label: 'Instructor',
      sortable: true,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 30, height: 30, borderRadius: 99, background: 'var(--brand-primary-l)',
              color: 'var(--brand-primary-d)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 11, flexShrink: 0,
            }}
          >
            {instrInitials(r)}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.EnglishFirstName} {r.EnglishLastName}</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.InstructorId}</div>
          </div>
        </div>
      ),
    },
    { key: 'JobTitle', label: lang === 'es' ? 'Cargo' : 'Title', muted: true, sortable: true },
    { key: 'country', label: lang === 'es' ? 'País' : 'Country', sortable: true, render: (r) => countryName(r.Country) },
    { key: 'cert', label: 'Cert.', sortable: true, render: (r) => <span className="chip">{r.Certification}</span> },
    {
      key: 'asg',
      label: lang === 'es' ? 'Asignaciones' : 'Assignments',
      align: 'right',
      sortable: true,
      render: (r) => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span className="mono" style={{ fontWeight: 600 }}>{r._asg}</span>
          {r._asg > 1 ? (
            <span className="chip brand" data-tip={lang === 'es' ? `También en otros ${r._asg - 1} site(s)` : `Also at ${r._asg - 1} other site(s)`}>
              +{r._asg - 1}
            </span>
          ) : null}
        </div>
      ),
    },
    { key: 'go', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-3)' }}>{ic.chevR}</span> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
        <span style={{ color: 'var(--brand-primary)', display: 'inline-flex' }}>{sized(ic.instr, 16)}</span>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{lang === 'es' ? 'Instructores del site' : 'Site instructors'}</div>
        <span className="chip">{list.length}</span>
      </div>

      {list.length === 0 ? (
        <div className="card" style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-3)' }}>
          <div style={{ display: 'inline-flex', marginBottom: 8, color: 'var(--line-2)' }}>{sized(ic.instr, 28)}</div>
          <div style={{ fontSize: 13 }}>
            {lang === 'es' ? 'Aún no hay instructores asignados a este site.' : 'No instructors assigned to this site yet.'}
          </div>
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            sort={table.sort}
            onSort={table.onSort}
            stickyHeader
            onRowClick={(i) => go('instructorDetail', { instructorId: i.InstructorId })}
          />
          <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={list.length} per={table.per} onPer={table.onPer} perOptions={[5, 10, 25, 50]} />
        </>
      )}
    </div>
  );
}
